/**
 * History panel, a dock tab of the Chart workspace (W2-10 projects-data; DESIGN.md §4.18, §5.13).
 *
 * Every undoable change with its label, icon and time, newest at the top; the current position is marked and undone
 * entries (still redo-able) are greyed. Clicking an entry (or Enter on it) goes there: later entries stay redo-able
 * until the next edit; "jump to the change on undo/redo" (settings.editing.jumpToChangeOnUndo) also scrolls to the
 * change and makes its line active. A divider marks where this session opened the chart; the bottom row is the state
 * before every listed change. The header shows the number of steps, their memory and that history survives reload
 * (the last 500 changes); when older changes are gone (memory cap, incomplete restore) the panel says "Earlier
 * changes: see Versions".
 */

import { useMemo } from 'react';
import type { PanelProps } from '../../app/registry/types';
import type { MessageKey } from '../../i18n/types';
import type { HistoryEntry } from '../../state/types';
import { Button, Checkbox, EmptyState, Icon, VirtualList } from '../../ui';
import { m } from './history.messages';
import {
  entryIcon,
  formatBytes,
  historyExtras,
  jumpTo,
  noEffect,
  relativeTime,
  revealFocus,
  shortcutText,
} from './historyModel';
import { sessionStart } from './session';
import { useHistoryVersion, useNow, useServicesStore, useSettingsValue, useText, type HistoryText } from './hooks';
import s from './History.module.css';

type Row =
  | { readonly kind: 'entry'; readonly entry: HistoryEntry; readonly index: number }
  | { readonly kind: 'opened' }
  | { readonly kind: 'start' };

const ROW_HEIGHT = 30;

function rowKey(r: Row): string {
  return r.kind === 'entry' ? `e${String(r.entry.seq)}` : r.kind;
}

function EntryRow(props: {
  readonly text: HistoryText;
  readonly row: Row;
  readonly position: number;
  readonly now: number;
}) {
  const { text, row, position, now } = props;
  if (row.kind === 'opened') {
    return (
      <span className={s.divider}>
        <span>{text.t(m.openedDivider)}</span>
      </span>
    );
  }
  if (row.kind === 'start') {
    const current = position === -1;
    return (
      <span className={s.row} data-current={current || undefined}>
        <span className={s.marker} aria-hidden="true">
          {current && <Icon name="ChevronRight" size={14} />}
        </span>
        <Icon name="Flag" size={14} />
        <span className={s.label}>{text.t(m.startRow)}</span>
        {current && <span className="visually-hidden">{text.t(m.currentSr)}</span>}
      </span>
    );
  }
  const { entry, index } = row;
  const current = index === position;
  const undone = entry.undone;
  const when = relativeTime(entry.at, now, text.locale) ?? text.t(m.justNow);
  return (
    <span className={s.row} data-current={current || undefined} data-undone={undone || undefined}>
      <span className={s.marker} aria-hidden="true">
        {current && <Icon name="ChevronRight" size={14} />}
      </span>
      <Icon name={entryIcon(entry.command)} size={14} />
      <span className={s.label}>{text.tr(entry.label)}</span>
      {noEffect(entry) && <span className={s.tag}>{text.t(m.noEffect)}</span>}
      <span className={s.time}>{when}</span>
      {current && <span className="visually-hidden">{text.t(m.currentSr)}</span>}
      {undone && <span className="visually-hidden">{text.t(m.undoneSr)}</span>}
    </span>
  );
}

export function HistoryPanel(props: PanelProps) {
  const { services } = props;
  const text = useText(services);
  const store = useServicesStore(services);
  const version = useHistoryVersion(store);
  const settings = useSettingsValue(services);
  const now = useNow();
  const jumpToChange = settings.editing.jumpToChangeOnUndo;

  const view = useMemo(() => {
    if (store === null) return null;
    const h = store.history;
    const entries = h.entries();
    return {
      entries,
      position: h.position,
      memoryBytes: h.memoryBytes,
      extras: historyExtras(h),
      version,
    };
  }, [store, version]);

  const rows = useMemo((): readonly Row[] => {
    if (view === null || store === null) return [];
    const opened = sessionStart(store);
    const out: Row[] = [];
    let divided = false;
    for (let i = view.entries.length - 1; i >= 0; i--) {
      const entry = view.entries[i];
      if (entry === undefined) continue;
      // Entries restored from an earlier session sit below the "opened" divider.
      if (!divided && entry.at < opened) {
        out.push({ kind: 'opened' });
        divided = true;
      }
      out.push({ kind: 'entry', entry, index: i });
    }
    out.push({ kind: 'start' });
    return out;
  }, [view, store]);

  if (store === null || view === null) {
    return (
      <div className={s.panel} data-region="history">
        <EmptyState icon="History" title={{ key: m.noProjectTitle }} body={{ key: m.noProjectBody }} />
      </div>
    );
  }

  const go = (position: number): void => {
    if (position === view.position) return;
    if (store.gestureActive) {
      services.ui.toast({ key: m.gestureActive }, { kind: 'info' });
      return;
    }
    const focus = jumpTo(store, position);
    if (jumpToChange && focus !== null) revealFocus(services, focus);
  };

  /** The shortcuts of a command in the user's keymap ("Ctrl+Z or Ctrl+Y"), else the name of its button. */
  const keysOf = (command: string, fallback: MessageKey): string =>
    shortcutText(services.keymap, text.locale, command) ?? text.t(fallback);

  const currentEntry = view.entries[view.position];
  const selected = new Set<string>([currentEntry === undefined ? 'start' : `e${String(currentEntry.seq)}`]);

  return (
    <div className={s.panel} data-region="history">
      <div className={s.header}>
        <span className={s.summary}>
          {text.t(m.summary, { count: view.entries.length, memory: formatBytes(view.memoryBytes, text) })}
        </span>
        <span className={s.headerActions}>
          <Checkbox
            label={text.t(m.jumpToChange)}
            checked={jumpToChange}
            onChange={(v) => {
              services.settings.set('editing', 'jumpToChangeOnUndo', v);
            }}
          />
          <Button size="sm" variant="ghost" icon="History" command="project.versions">
            {text.t(m.versionsButton)}
          </Button>
        </span>
      </div>
      {view.extras.truncated && (
        <p className={s.note}>
          <Icon name="Info" size={14} />
          <span>{text.t(m.truncated)}</span>
          <Button size="sm" variant="ghost" command="project.versions">
            {text.t(m.seeVersions)}
          </Button>
        </p>
      )}
      {view.entries.length === 0 ? (
        <EmptyState icon="History" title={{ key: m.emptyTitle }} body={{ key: m.emptyBody }} />
      ) : (
        <VirtualList<Row>
          className={s.list}
          items={rows}
          rowHeight={ROW_HEIGHT}
          getKey={rowKey}
          selected={selected}
          ariaLabel={{ key: m.listLabel }}
          render={(row) => <EntryRow text={text} row={row} position={view.position} now={now} />}
          onActivate={(row) => {
            if (row.kind === 'entry') go(row.index);
            else if (row.kind === 'start') go(-1);
          }}
        />
      )}
      <p className={s.hint}>
        {text.t(m.hint, { undo: keysOf('edit.undo', m.undoButton), redo: keysOf('edit.redo', m.redoButton) })}
      </p>
    </div>
  );
}
